import { Fragment } from 'react';
import { Link, useMatches } from '@tanstack/react-router';

import {
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Breadcrumb as BreadcrumbUI,
} from '@/components/ui/breadcrumb';

interface Crumb {
  href: string;
  title: string;
}

/**
 * Renders the breadcrumb trail attached to the deepest matched route that
 * supplies one via its `beforeLoad` context. The deepest match wins because
 * child routes typically extend the parent trail.
 */
export function Breadcrumb() {
  const trail = useBreadcrumbTrail();

  if (!trail || trail.length === 0) {
    return null;
  }

  return (
    <BreadcrumbUI>
      <BreadcrumbList>
        {trail.map((crumb, index) => {
          const isLast = index === trail.length - 1;
          return (
            <Fragment key={crumb.href ?? crumb.title}>
              <BreadcrumbItem>
                {isLast ? (
                  <BreadcrumbPage className="font-medium">{crumb.title}</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link to={crumb.href}>{crumb.title}</Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
              {!isLast && <BreadcrumbSeparator />}
            </Fragment>
          );
        })}
      </BreadcrumbList>
    </BreadcrumbUI>
  );
}

function useBreadcrumbTrail(): Crumb[] | undefined {
  const matches = useMatches();
  // Walk matches deepest-first without mutating the array the router gave us.
  for (let i = matches.length - 1; i >= 0; i--) {
    const ctx = matches[i].context as { breadcrumbs?: Crumb[] } | undefined;
    if (ctx?.breadcrumbs) return ctx.breadcrumbs;
  }
  return undefined;
}
